import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, CheckCircle, Loader2, Search, Sparkles, X } from 'lucide-react';
import type { TFunction } from 'i18next';

import { Button } from '@/components/common/Button';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import {
  useGetLibrariesQuery,
  type AnimationLibrary,
  type AvatarMesh,
  type LibraryCatalogEntry,
} from '@/services/avatarApi';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

type LibraryState = 'idle' | 'owned' | 'in-flight' | 'incompatible';

function packStateFor(
  library: AnimationLibrary,
  libraryProvider: 'tripo' | 'meshy',
  mesh: AvatarMesh | undefined,
): LibraryState {
  if (!mesh) return 'idle';
  const inflight = new Set([
    'animating_queued','animating_rigging','animating_retargeting',
    'animating_merging','animating_uploading',
  ]);
  if (inflight.has(mesh.status as string)) return 'in-flight';
  const pack = (mesh.animationPacks ?? []).find(p => p.library === library);
  if (pack && pack.status === 'ready') return 'owned';
  const meshProvider = (mesh.provider as 'tripo' | 'meshy' | undefined) ?? 'tripo';
  if (meshProvider !== libraryProvider) return 'incompatible';
  return 'idle';
}

function humanize(id: string, t: TFunction): string {
  // Fallback for catalog labels that come back as preset ids (e.g. 'meshy-dance-22').
  const prettify = (s: string) => s.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const dance = id.match(/^meshy-dance-(.*)$/);
  if (dance) return t('animateLibrary.danceN', { n: prettify(dance[1]) });
  return prettify(id.replace(/^meshy-/, ''));
}

interface Props {
  open: boolean;
  mesh?: AvatarMesh;
  /**
   * Phase 3 closure — providers the avatar's source template allows.
   * Undefined → permissive (legacy avatars without templateId, or templates
   * whose admin hasn't set the field yet). Empty array → no providers allowed
   * (admin opted out completely).
   */
  compatibleProviders?: Array<'tripo' | 'meshy'>;
  onClose: () => void;
  /** Phase 4 — `presetIds` is the user's movement subset (omit for full pack). */
  onPick: (library: AnimationLibrary, force: boolean, presetIds?: string[]) => void;
}

export function AnimateLibraryModal({ open, mesh, compatibleProviders, onClose, onPick }: Props) {
  const { t } = useTranslation('avatars');
  useLivelloIndietro(open, onClose);
  const haptics = useHaptics();
  // `refetchOnMountOrArgChange: 30` forces a refetch when the modal reopens
  // after >30s, picking up new fields the backend may have added since the
  // last fetch (e.g. `presets[]` added in Phase 4). Without this RTK Query
  // would happily serve a stale cached entry missing those fields.
  const { data, isLoading } = useGetLibrariesQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: 30,
  });
  const [step, setStep] = useState<'libraries' | 'presets'>('libraries');
  const [selectedLibrary, setSelectedLibrary] = useState<LibraryCatalogEntry | null>(null);
  const [selectedPresetIds, setSelectedPresetIds] = useState<Set<string>>(new Set());

  // Reset to library list whenever the modal reopens (avoids landing on a
  // stale preset selection from a previous session).
  useEffect(() => {
    if (open) {
      setStep('libraries');
      setSelectedLibrary(null);
      setSelectedPresetIds(new Set());
    }
  }, [open]);

  const presetCount = selectedPresetIds.size;
  // NOTE: every hook (incl. useMemo) MUST run before any early return below.
  // Returning null earlier and then calling useMemo would change the hook
  // count between renders (React: "Rendered more hooks than during the
  // previous render").
  const liveCostUsd = useMemo(() => {
    if (!selectedLibrary) return 0;
    return selectedLibrary.rigCostUsd + presetCount * selectedLibrary.perClipCostUsd;
  }, [selectedLibrary, presetCount]);

  if (!open) return null;
  // Permissive default: missing/undefined `compatibleProviders` = let everything
  // through (legacy avatars + templates the admin hasn't curated yet).
  const allowedProviders = Array.isArray(compatibleProviders) && compatibleProviders.length > 0
    ? new Set(compatibleProviders)
    : null;
  const allLibraries: LibraryCatalogEntry[] = data?.libraries ?? [];
  const libraries: LibraryCatalogEntry[] = allowedProviders
    ? allLibraries.filter(l => allowedProviders.has(l.provider))
    : allLibraries;

  const openPresetPicker = (lib: LibraryCatalogEntry) => {
    haptics.impact('medium');
    // Backward-compat: older /libraries payloads (pre Phase 4) didn't expose
    // `presets[]`. Treat the missing case as "no granular picker available"
    // and bail out gracefully instead of crashing — caller can still tap
    // again after a refetch.
    if (!Array.isArray(lib.presets) || lib.presets.length === 0) {
      console.warn('[AnimateLibraryModal] library has no presets[]', lib.library);
      return;
    }
    setSelectedLibrary(lib);
    // Default selection:
    //  - Small curated libraries (Action Pack, Dancing Pack): every preset
    //    selected → matches "Generate all" intent for a small pack.
    //  - Phase 6 meshy-full (556 presets): start EMPTY, user cherry-picks.
    //    Selecting everything by default would silently trigger a ~$33 retarget.
    const isLargeLibrary = lib.library === 'meshy-full';
    setSelectedPresetIds(new Set(isLargeLibrary ? [] : lib.presets.map(p => p.id)));
    setStep('presets');
  };

  const handleLibraryTap = (lib: LibraryCatalogEntry) => {
    if (!lib.enabled) return;
    const state = packStateFor(lib.library, lib.provider, mesh);
    if (state === 'in-flight' || state === 'incompatible') return;
    openPresetPicker(lib);
  };

  const togglePreset = (id: string) => {
    haptics.selection();
    setSelectedPresetIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!selectedLibrary?.presets) return;
    haptics.selection();
    const allIds = selectedLibrary.presets.map(p => p.id);
    const allSelected = allIds.every(id => selectedPresetIds.has(id));
    setSelectedPresetIds(allSelected ? new Set() : new Set(allIds));
  };

  const isFullSelection =
    selectedLibrary?.presets != null && presetCount === selectedLibrary.presets.length;

  const handleGenerate = () => {
    if (!selectedLibrary || presetCount === 0) return;
    haptics.impact('medium');
    const state = packStateFor(selectedLibrary.library, selectedLibrary.provider, mesh);
    const ids = isFullSelection ? undefined : Array.from(selectedPresetIds);
    onPick(selectedLibrary.library, state === 'owned', ids);
  };

  const onBackToLibraries = () => {
    haptics.impact('light');
    setStep('libraries');
    setSelectedLibrary(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-center justify-between px-4 py-4">
        <div className="flex items-center gap-2">
          {step === 'presets' && (
            <Button
              variant="icona"
              size={36}
              tono="pieno"
              aria-label={t('common.back')}
              onClick={onBackToLibraries}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div>
            <h1 className="text-lg font-bold text-fg">
              {step === 'libraries' ? t('animateLibrary.title') : `${selectedLibrary?.label}`}
            </h1>
            <p className="mt-0.5 text-xs text-fg-muted">
              {step === 'libraries'
                ? t('animateLibrary.subtitle')
                : t('animateLibrary.presetsSubtitle')}
            </p>
          </div>
        </div>
        <Button
          variant="icona"
          size={36}
          tono="pieno"
          aria-label={t('common.close')}
          onClick={() => { haptics.impact('light'); onClose(); }}
        >
          <X className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-32">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-fg-muted" />
          </div>
        ) : step === 'libraries' ? (
          libraries.length === 0 ? (
            <p className="py-10 text-center text-sm text-fg-muted">{t('animateLibrary.empty')}</p>
          ) : (
            <ul className="grid grid-cols-2 gap-3">
              {libraries.map(lib => {
                const state = packStateFor(lib.library, lib.provider, mesh);
                const ctaLabel =
                  state === 'owned' ? t('animateLibrary.cta.regenerate')
                  : state === 'in-flight' ? t('animateLibrary.cta.generating')
                  : state === 'incompatible' ? t('animateLibrary.cta.needsMesh', { provider: lib.provider === 'meshy' ? 'Meshy' : 'Tripo' })
                  : t('animateLibrary.cta.pick');
                return (
                  <li key={lib.library}>
                    <button
                      type="button"
                      onClick={() => handleLibraryTap(lib)}
                      disabled={state === 'in-flight' || state === 'incompatible'}
                      className={cn(
                        'flex w-full flex-col items-start gap-2 rounded-2xl border p-3 text-left transition-opacity active:opacity-80',
                        state === 'owned' ? 'border-brand bg-brand/10' : 'border-border bg-bg-secondary',
                        (state === 'in-flight' || state === 'incompatible') && 'opacity-50',
                      )}
                    >
                      <div className="flex w-full items-center justify-between">
                        <span className="text-sm font-bold text-fg">{lib.label}</span>
                        {state === 'owned' && <CheckCircle className="h-4 w-4 text-brand" aria-label={t('animateLibrary.owned')} />}
                        {state === 'in-flight' && <Loader2 className="h-4 w-4 animate-spin text-fg-muted" />}
                        {state === 'incompatible' && <AlertTriangle className="h-4 w-4 text-amber-400" aria-label={t('animateLibrary.incompatible')} />}
                        {state === 'idle' && <Sparkles className="h-4 w-4 text-fg-muted" />}
                      </div>
                      <span className="text-[11px] text-fg-muted">
                        {t('animateLibrary.stats', { count: lib.presetCount, minutes: lib.estimatedMinutes, cost: lib.estimatedCostUsd.toFixed(2) })}
                      </span>
                      <span className={cn(
                        'text-[11px] font-semibold',
                        state === 'incompatible' ? 'text-amber-400' : 'text-brand',
                      )}>
                        {ctaLabel}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )
        ) : (
          // Step 2: preset multi-select. Flat list for small libraries;
          // categorized picker (tabs + search) for meshy-full.
          selectedLibrary && Array.isArray(selectedLibrary.presets) && (
            selectedLibrary.library === 'meshy-full'
              ? renderCategorizedPicker(selectedLibrary, selectedPresetIds, togglePreset)
              : renderFlatPicker(selectedLibrary, selectedPresetIds, togglePreset, toggleAll, isFullSelection, presetCount, t)
          )
        )}
      </div>

      {/* Sticky Generate footer (only on preset step) */}
      {step === 'presets' && selectedLibrary && (
        <div className="absolute inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur">
          <Button fullWidth onClick={handleGenerate} disabled={presetCount === 0}>
            <Sparkles aria-hidden className="h-4 w-4" />
            {t('animateLibrary.generate', { count: presetCount, cost: liveCostUsd.toFixed(2) })}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function renderFlatPicker(
  lib: LibraryCatalogEntry,
  selectedIds: Set<string>,
  toggle: (id: string) => void,
  toggleAll: () => void,
  isFullSelection: boolean,
  presetCount: number,
  t: TFunction,
) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between rounded-xl bg-bg-secondary px-3 py-2">
        <p className="text-xs text-fg-muted">
          <span className="font-bold text-fg">{presetCount}</span> {t('animateLibrary.ofTotalSelected', { total: lib.presets.length })}
        </p>
        <button
          type="button"
          onClick={toggleAll}
          className="rounded-full bg-bg px-3 py-1 text-[11px] font-semibold text-fg"
        >
          {isFullSelection ? t('animateLibrary.clearAll') : t('animateLibrary.selectAll')}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {lib.presets.map(p => {
          const isSel = selectedIds.has(p.id);
          return (
            <Button key={p.id} variant="scelta" pressed={isSel} onClick={() => toggle(p.id)}>
              {p.label || humanize(p.id, t)}
            </Button>
          );
        })}
      </div>

      <p className="text-[11px] leading-relaxed text-fg-muted">
        {t('animateLibrary.rigNote', { rig: lib.rigCostUsd.toFixed(2), perClip: lib.perClipCostUsd.toFixed(2) })}
      </p>
    </div>
  );
}

// Phase 6 — soft cap on per-pack selection to keep retarget cost predictable
// (each retarget is ~$0.06 on Meshy Pro; 20 presets ≈ $1.32 incl. rig).
const MESHY_FULL_MAX_PRESETS = 20;

function CategorizedPicker({
  lib,
  selectedIds,
  toggle,
}: {
  lib: LibraryCatalogEntry;
  selectedIds: Set<string>;
  toggle: (id: string) => void;
}) {
  const { t } = useTranslation('avatars');
  const categories = useMemo(() => {
    const set = new Set<string>();
    lib.presets.forEach(p => { if (p.category) set.add(p.category); });
    return Array.from(set);
  }, [lib]);

  const [activeCategory, setActiveCategory] = useState<string>(categories[0] ?? '');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (categories.length > 0 && !categories.includes(activeCategory)) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  const visiblePresets = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lib.presets.filter(p => {
      if (!q && p.category !== activeCategory) return false;
      if (q && !`${p.label} ${p.id}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [lib.presets, activeCategory, search]);

  const subcategoryGroups = useMemo(() => {
    const groups = new Map<string, typeof lib.presets>();
    visiblePresets.forEach(p => {
      const key = p.subcategory ?? t('animateLibrary.otherGroup');
      const arr = groups.get(key) ?? [];
      arr.push(p);
      groups.set(key, arr);
    });
    return Array.from(groups.entries());
  }, [visiblePresets, t]);

  const overCap = selectedIds.size >= MESHY_FULL_MAX_PRESETS;

  return (
    <div className="flex flex-col gap-3">
      {/* Sticky category tabs */}
      <div className="sticky top-0 z-10 -mx-4 bg-bg/95 px-4 pb-2 pt-1 backdrop-blur">
        <div className="flex gap-1 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {categories.map(cat => {
            const active = cat === activeCategory && !search;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => { setActiveCategory(cat); setSearch(''); }}
                className={cn(
                  'whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                  active ? 'bg-brand text-brand-fg' : 'bg-bg-secondary text-fg-muted',
                )}
              >
                {cat}
              </button>
            );
          })}
        </div>

        <div className="mt-2 flex items-center gap-2 rounded-xl bg-bg-secondary px-3 py-2">
          <Search aria-hidden className="h-3.5 w-3.5 text-fg-muted" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('animateLibrary.searchPlaceholder', { count: lib.presets.length })}
            className="flex-1 bg-transparent text-xs text-fg placeholder:text-fg-muted/60 focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label={t('animateLibrary.clearSearch')}
              className="text-fg-muted active:opacity-70"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="mt-2 flex items-center justify-between rounded-lg bg-bg-secondary/60 px-2 py-1.5 text-[11px]">
          <span className={cn(overCap ? 'text-amber-400' : 'text-fg-muted')}>
            <span className="font-bold text-fg">{selectedIds.size}</span>
            {' / '}
            {t('animateLibrary.maxSelected', { max: MESHY_FULL_MAX_PRESETS })}
            {overCap && ` · ${t('animateLibrary.capReached')}`}
          </span>
          <span className="text-fg-muted">
            ~${(lib.rigCostUsd + selectedIds.size * lib.perClipCostUsd).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Preset list grouped by subcategory */}
      {visiblePresets.length === 0 ? (
        <p className="py-10 text-center text-xs text-fg-muted">{t('animateLibrary.noMatches')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {subcategoryGroups.map(([sub, presets]) => (
            <div key={sub} className="flex flex-col gap-1.5">
              <p className="px-1 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
                {sub}
              </p>
              <div className="flex flex-wrap gap-2">
                {presets.map(p => {
                  const isSel = selectedIds.has(p.id);
                  const disabled = !isSel && overCap;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={isSel}
                      disabled={disabled}
                      onClick={() => toggle(p.id)}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                        isSel
                          ? 'border-brand bg-brand text-brand-fg'
                          : disabled
                            ? 'border-border bg-bg-secondary text-fg-muted/40'
                            : 'border-border bg-bg-secondary text-fg',
                      )}
                    >
                      {p.label || humanize(p.id, t)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderCategorizedPicker(
  lib: LibraryCatalogEntry,
  selectedIds: Set<string>,
  toggle: (id: string) => void,
) {
  return <CategorizedPicker lib={lib} selectedIds={selectedIds} toggle={toggle} />;
}
